/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { MouseEvent } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import SyntaxHighlighter from "react-syntax-highlighter";
import {
  atomOneDark,
  github,
} from "react-syntax-highlighter/dist/esm/styles/hljs";
import { Button, FlexContainer, Box, theme } from "@nordcloud/gnui";
import { SUCCESS_TEXT } from "~/constants";
import { useGNUITheme } from "~/hooks/useGNUITheme";
import { showInfo } from "~/services/toast";

type Props = {
  code: string;
  language: string;
  wrapLongLines?: boolean;
};

export function CodeHighlighter({ code, language, wrapLongLines }: Props) {
  const { isLight } = useGNUITheme();
  const syntaxStyle = isLight
    ? { ...github, ...{ hljs: { background: "none" } } }
    : { ...atomOneDark, ...{ hljs: { background: "none" } } };

  return (
    <Box boxStyle="lightGrey" spacing="spacing04">
      <FlexContainer alignItems="start" justifyContent="space-between">
        <SyntaxHighlighter
          language={language}
          style={syntaxStyle}
          lineProps={{
            style: {
              wordBreak: wrapLongLines ? "break-all" : "normal",
              overflowWrap: wrapLongLines ? "anywhere" : "normal",
              fontFamily: "monospace",
            },
          }}
          wrapLines
          showLineNumbers
          lineNumberStyle={{ color: theme.colors.darks[4] }}
          wrapLongLines={wrapLongLines}
        >
          {code}
        </SyntaxHighlighter>
        <CopyToClipboard
          text={code}
          onCopy={() => {
            showInfo(SUCCESS_TEXT.copied);
          }}
          css={{
            position: "sticky",
            top: "10px",
          }}
        >
          <Button
            icon="copy"
            size="md"
            severity="low"
            onClick={(event: MouseEvent<HTMLElement>) => event.preventDefault()}
          />
        </CopyToClipboard>
      </FlexContainer>
    </Box>
  );
}
